import * as React from "react";
import { Check, ChevronsUpDown, X, Loader2, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGeneratorsSearch } from "@/hooks/useGeneratorsSearch";

export interface SearchableGeneratorSelectProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
}

export const SearchableGeneratorSelect = React.forwardRef<HTMLButtonElement, SearchableGeneratorSelectProps>(
  (
    {
      value,
      onChange,
      placeholder = "Selecionar gerador...",
      searchPlaceholder = "Buscar gerador...",
      emptyMessage = "Nenhum gerador encontrado.",
      disabled = false,
      className,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [searchQuery, setSearchQuery] = React.useState("");
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Busca todos os geradores quando o popover abre
    const { data: generators = [], isLoading } = useGeneratorsSearch({
      searchQuery: "", // Sempre busca todos
      enabled: open,
    });

    // Filtragem local instantânea baseada no texto digitado
    const filteredGenerators = React.useMemo(() => {
      if (!searchQuery.trim()) {
        return generators;
      }
      const searchLower = searchQuery.trim().toLowerCase();
      return generators.filter((gen) =>
        gen.generator_name.toLowerCase().includes(searchLower)
      );
    }, [generators, searchQuery]);

    const selectedGenerator = React.useMemo(
      () => generators.find((gen) => gen.generator_id === value),
      [generators, value]
    );

    const handleSelect = (generatorId: string) => {
      if (value === generatorId) {
        onChange(undefined);
      } else {
        onChange(generatorId);
      }
      setOpen(false);
      setSearchQuery("");
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(undefined);
    };

    // Foca no input quando o popover abre
    React.useEffect(() => {
      if (open && inputRef.current) {
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }, [open]);

    // Limpa a busca quando fecha
    React.useEffect(() => {
      if (!open) {
        setSearchQuery("");
      }
    }, [open]);

    return (
      <div className={cn("space-y-2", className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              ref={ref}
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className={cn(
                "w-full justify-between bg-background",
                !selectedGenerator && "text-muted-foreground"
              )}
            >
              <span className="truncate">
                {selectedGenerator ? selectedGenerator.generator_name : placeholder}
              </span>
              <div className="flex items-center gap-2">
                {selectedGenerator && !disabled && (
                  <X
                    className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                    onClick={handleClear}
                  />
                )}
                <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-[var(--radix-popover-trigger-width)] p-0 bg-popover border shadow-md z-50" 
            align="start"
            side="bottom"
          >
            {/* Search Input */}
            <div className="flex items-center border-b px-3 py-2">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <Input
                ref={inputRef}
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 border-0 bg-transparent p-0 text-sm placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              {searchQuery && (
                <X
                  className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100 cursor-pointer"
                  onClick={() => setSearchQuery("")}
                />
              )}
            </div>

            {/* Results List */}
            <ScrollArea className="max-h-[300px]">
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="ml-2 text-sm text-muted-foreground">Carregando...</span>
                </div>
              ) : filteredGenerators.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {searchQuery.trim() 
                    ? `Nenhum resultado para "${searchQuery}"` 
                    : emptyMessage}
                </div>
              ) : (
                <div className="p-1">
                  {filteredGenerators.map((generator) => (
                    <div
                      key={generator.generator_id}
                      onClick={() => handleSelect(generator.generator_id)}
                      className={cn(
                        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
                        "hover:bg-accent hover:text-accent-foreground",
                        value === generator.generator_id && "bg-accent"
                      )}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 shrink-0",
                          value === generator.generator_id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span className="truncate">{generator.generator_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            {/* Results count */}
            {!isLoading && filteredGenerators.length > 0 && (
              <div className="border-t px-3 py-2 text-xs text-muted-foreground">
                {searchQuery.trim() 
                  ? `${filteredGenerators.length} resultado${filteredGenerators.length !== 1 ? 's' : ''}`
                  : `${generators.length} gerador${generators.length !== 1 ? 'es' : ''}`
                }
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);

SearchableGeneratorSelect.displayName = "SearchableGeneratorSelect";
